"use client";

// ============================================================
// CLUBHUB – Spende erfassen und bearbeiten
// ============================================================
// EIN Dialog fuer alle drei Arten. Was eine Art zusaetzlich
// braucht, steht erst da, wenn sie gewaehlt ist:
//
//   Geld               Zahlungsart
//   Sachspende         Bezeichnung, Alter und Zustand, Wert-
//                      ermittlung, Herkunft — genau die Felder des
//                      amtlichen Musters „Sachzuwendungen“
//   Aufwandsverzicht   der Erstattungsanspruch, auf den verzichtet
//                      wird: worauf, auf welcher Grundlage, seit
//                      wann — und wann verzichtet wurde
//
// Die Pruefung ist dieselbe wie in der Action (`lib/spenden/pruefen`):
// was dort durchfaellt, steht hier am Feld.
// ============================================================

import { useState } from "react";
import { Info, Plus } from "lucide-react";
import type { DonationKind, PaymentMethod } from "@/generated/prisma/enums";
import {
  AmountInput,
  Button,
  Checkbox,
  Dialog,
  DialogCancel,
  DialogChoice,
  DialogColumn,
  DialogColumns,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogRow,
  DialogSection,
  DialogSubmit,
  DialogTitle,
  FieldLabel,
  Input,
  Select,
  Textarea,
  toast,
  type ButtonProps,
} from "@/components/ui";
import { KontaktAuswahl } from "@/components/mitglieder/kontakt-auswahl";
import { PAYMENT_METHOD } from "@/lib/labels";
import { ANSPRUCH_GRUNDLAGE, ART_HINWEIS, HERKUNFT, WERTERMITTLUNG, cent, euro, heuteIso } from "@/lib/spenden/format";
import type { AnspruchsGrundlage, Herkunft, SpendeView, Wertermittlung } from "@/lib/spenden/views";
import { spendeSpeichern } from "@/app/(app)/spenden/actions";

export type AufrufWahl = { id: string; title: string; purpose: string | null; laufend: boolean };

type Vorgabe = { contactId?: string | null; campaignId?: string | null };

function Feldfehler({ text }: { text?: string }) {
  if (!text) return null;
  return (
    <p className="text-caption text-danger mt-1.5" role="alert">
      {text}
    </p>
  );
}

export function SpendeDialog({
  open,
  onClose,
  spende,
  vorgabe,
  aufrufe,
}: {
  open: boolean;
  onClose: () => void;
  /** Gesetzt: bearbeiten. */
  spende?: SpendeView | null;
  vorgabe?: Vorgabe;
  aufrufe: AufrufWahl[];
}) {
  const bestaetigt = Boolean(spende?.receiptId);
  const [spender, setSpender] = useState<"person" | "anonym">(spende && !spende.contactId ? "anonym" : "person");
  const [contactId, setContactId] = useState<string | null>(spende?.contactId ?? vorgabe?.contactId ?? null);
  const [kind, setKind] = useState<DonationKind>(spende?.kind ?? "MONEY");
  const [method, setMethod] = useState<PaymentMethod>(spende?.kind === "MONEY" ? spende.method : "TRANSFER");
  const [campaignId, setCampaignId] = useState(spende?.campaignId ?? vorgabe?.campaignId ?? "");
  const vorZweck = aufrufe.find((a) => a.id === (spende?.campaignId ?? vorgabe?.campaignId))?.purpose ?? "";
  const [purpose, setPurpose] = useState(spende?.purpose ?? vorZweck);
  const [herkunft, setHerkunft] = useState<Herkunft>(spende?.sach?.herkunft ?? "PRIVAT");
  const [wertermittlung, setWertermittlung] = useState<Wertermittlung>(spende?.sach?.wertermittlung ?? "RECHNUNG");
  const [grundlage, setGrundlage] = useState<AnspruchsGrundlage>(spende?.verzicht?.grundlage ?? "BESCHLUSS");
  const [betragCent, setBetragCent] = useState<number | null>(cent(spende?.amount));
  const [fehler, setFehler] = useState<Record<string, string>>({});

  const heute = heuteIso();
  const aufrufOptionen = [
    { value: "", label: "Kein Aufruf" },
    ...aufrufe
      .filter((a) => a.laufend || a.id === campaignId)
      .map((a) => ({ value: a.id, label: a.title, hint: a.purpose ?? undefined })),
  ];

  function aufrufWaehlen(id: string) {
    setCampaignId(id);
    const alt = aufrufe.find((a) => a.id === campaignId)?.purpose ?? "";
    const neu = aufrufe.find((a) => a.id === id)?.purpose ?? "";
    // Den Zweck nur nachziehen, solange er der des vorigen Aufrufs war.
    if (!purpose || purpose === alt) setPurpose(neu);
  }

  const betragLabel = kind === "MONEY" ? "Betrag" : kind === "IN_KIND" ? "Wert der Sachspende" : "Höhe des Anspruchs";

  return (
    <Dialog open={open} onClose={onClose} size="wide">
      <DialogForm
        action={(fd) => spendeSpeichern(spende?.id ?? null, fd)}
        success={(r) =>
          "id" in r
            ? spende
              ? "Spende gespeichert"
              : { title: "Spende erfasst", description: `${betragCent ? euro(betragCent / 100) : ""} · ${r.text}` }
            : null
        }
        onError={(message, result) => {
          const field = result && "field" in result ? result.field : undefined;
          if (field) setFehler({ [field]: message });
          else {
            setFehler({});
            toast.error(message);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>{spende ? "Spende bearbeiten" : "Spende erfassen"}</DialogTitle>
          <DialogDescription>
            {spende
              ? bestaetigt
                ? `Bestätigt mit ${spende.receiptNumber}. Zweck, Aufruf und Notiz lassen sich ändern.`
                : "Änderungen gelten sofort, auch für die Summen des Aufrufs."
              : "Was eingegangen ist — Geld, ein Gegenstand oder der Verzicht auf eine Erstattung."}
          </DialogDescription>
        </DialogHeader>

        <DialogContent>
          {bestaetigt && (
            <DialogNotice tone="warning" icon={<Info className="text-warning h-4 w-4" aria-hidden />}>
              Person, Art, Betrag und Datum stehen auf der Bestätigung {spende?.receiptNumber}. Sie ändern sich erst, wenn die Bestätigung widerrufen ist.
            </DialogNotice>
          )}

          <DialogColumns>
            <DialogColumn>
              <div>
                <DialogChoice
                  label="Von wem"
                  name="spender"
                  value={spender}
                  onChange={(v) => {
                    if (bestaetigt) return;
                    setSpender(v);
                    if (v === "anonym" && kind === "WAIVER") setKind("MONEY");
                  }}
                  options={[
                    { value: "person", label: "Person oder Firma", hint: "Aus Mitgliedern und Kontakten — bekommt die Bestätigung" },
                    { value: "anonym", label: "Ohne Namen", hint: "Spendenbox, Barspende ohne Angabe — keine Bestätigung möglich" },
                  ]}
                />
              </div>

              {spender === "person" && (
                <div className="space-y-3">
                  <div>
                    <FieldLabel>Person oder Firma</FieldLabel>
                    <div className="mt-1.5">
                      <KontaktAuswahl
                        value={contactId}
                        onChange={(id) => {
                          setContactId(id);
                          setFehler((f) => ({ ...f, contactId: "" }));
                        }}
                        disabled={bestaetigt}
                        placeholder="Name, Nummer, Mail, Ort …"
                      />
                    </div>
                    <input type="hidden" name="contactId" value={contactId ?? ""} />
                    <Feldfehler text={fehler.contactId} />
                  </div>
                  <Checkbox
                    name="isAnonymous"
                    defaultChecked={spende ? spende.isAnonymous && Boolean(spende.contactId) : false}
                    label="Nicht namentlich nennen"
                    hint="Erscheint in Listen, beim Aufruf und auf der Website als „Anonym“. Die Bestätigung bekommt die Person trotzdem."
                  />
                </div>
              )}

              <div>
                <DialogChoice
                  label="Art der Spende"
                  name="kind"
                  value={kind}
                  onChange={(v) => {
                    if (bestaetigt) return;
                    setKind(v);
                    setFehler({});
                  }}
                  options={[
                    { value: "MONEY", label: "Geldspende", hint: ART_HINWEIS.MONEY },
                    { value: "IN_KIND", label: "Sachspende", hint: ART_HINWEIS.IN_KIND },
                    ...(spender === "person" ? [{ value: "WAIVER" as const, label: "Aufwandsverzicht", hint: ART_HINWEIS.WAIVER }] : []),
                  ]}
                />
                <Feldfehler text={fehler.kind} />
              </div>
            </DialogColumn>

            <DialogColumn>
              <DialogRow>
                <Input
                  name="date"
                  type="date"
                  label={kind === "WAIVER" ? "Tag der Zuwendung" : "Eingegangen am"}
                  defaultValue={spende?.date ?? heute}
                  max={heute}
                  required
                  readOnly={bestaetigt}
                  error={fehler.date}
                />
                <AmountInput
                  name="amount"
                  label={`${betragLabel} (€)`}
                  defaultCents={cent(spende?.amount)}
                  onValueChange={setBetragCent}
                  required
                  readOnly={bestaetigt}
                  className="numeric text-right"
                  error={fehler.amount}
                />
              </DialogRow>

              {kind === "MONEY" ? (
                <Select
                  name="method"
                  label="Zahlungsart"
                  value={method}
                  onChange={(v) => setMethod(v as PaymentMethod)}
                  searchable={false}
                  options={(Object.keys(PAYMENT_METHOD) as PaymentMethod[]).map((m) => ({ value: m, label: PAYMENT_METHOD[m] }))}
                  error={fehler.method}
                />
              ) : (
                <input type="hidden" name="method" value="OTHER" />
              )}

              <Select
                name="campaignId"
                label="Spendenaufruf"
                value={campaignId}
                onChange={aufrufWaehlen}
                options={aufrufOptionen}
                hint="Zählt beim Fortschritt des Aufrufs mit."
                error={fehler.campaignId}
              />
              <Input
                name="purpose"
                label="Zweck"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value)}
                placeholder="Jugendarbeit, Platzpflege, Allgemeine Vereinsarbeit …"
                maxLength={200}
                error={fehler.purpose}
              />
              <Textarea name="note" label="Notiz (intern)" defaultValue={spende?.note ?? ""} rows={2} maxLength={1000} error={fehler.note} />
            </DialogColumn>
          </DialogColumns>

          {kind === "IN_KIND" && (
            <DialogSection>
              <p className="text-overline text-text-tertiary">Angaben für die Bestätigung über Sachzuwendungen</p>
              <Input
                name="bezeichnung"
                label="Genaue Bezeichnung"
                defaultValue={spende?.sach?.bezeichnung ?? ""}
                placeholder="20 Fußbälle Größe 4"
                required
                readOnly={bestaetigt}
                error={fehler.bezeichnung}
              />
              <DialogRow>
                <Input
                  name="alterZustand"
                  label="Alter und Zustand"
                  defaultValue={spende?.sach?.alterZustand ?? ""}
                  placeholder="neu · gebraucht, 2 Jahre, voll funktionsfähig"
                  required
                  readOnly={bestaetigt}
                  error={fehler.alterZustand}
                />
                <Select
                  name="wertermittlung"
                  label="Wert ermittelt über"
                  value={wertermittlung}
                  onChange={(v) => setWertermittlung(v as Wertermittlung)}
                  searchable={false}
                  options={(Object.keys(WERTERMITTLUNG) as Wertermittlung[]).map((w) => ({ value: w, label: WERTERMITTLUNG[w] }))}
                  error={fehler.wertermittlung}
                />
              </DialogRow>
              <Input
                name="wertGrundlage"
                label="Grundlage des Werts"
                defaultValue={spende?.sach?.wertGrundlage ?? ""}
                placeholder="Rechnung Sporthaus vom 28.09.2024"
                hint="Welcher Beleg, welches Gutachten, welche Vergleichsangebote — für die Unterlagen des Vereins."
              />
              <DialogChoice
                label="Herkunft"
                name="herkunft"
                value={herkunft}
                onChange={(v) => !bestaetigt && setHerkunft(v)}
                options={(Object.keys(HERKUNFT) as Herkunft[]).map((h) => ({ value: h, label: HERKUNFT[h].label, hint: HERKUNFT[h].hinweis }))}
              />
              <Feldfehler text={fehler.herkunft} />
              <Checkbox
                name="unterlagen"
                defaultChecked={spende?.sach?.unterlagenLiegenVor ?? false}
                label="Unterlagen zur Wertermittlung liegen vor"
                hint="Rechnung, Gutachten oder ausgedruckte Vergleichsangebote — das Muster fragt danach."
              />
            </DialogSection>
          )}

          {kind === "WAIVER" && (
            <DialogSection>
              <p className="text-overline text-text-tertiary">Der Erstattungsanspruch, auf den verzichtet wird</p>
              <Input
                name="anspruch"
                label="Worauf besteht der Anspruch"
                defaultValue={spende?.verzicht?.anspruch ?? ""}
                placeholder="Fahrtkosten zu Auswärtsspielen, 960 km × 0,30 €"
                required
                readOnly={bestaetigt}
                error={fehler.anspruch}
              />
              <DialogRow columns={3}>
                <Select
                  name="grundlage"
                  label="Grundlage"
                  value={grundlage}
                  onChange={(v) => setGrundlage(v as AnspruchsGrundlage)}
                  searchable={false}
                  options={(Object.keys(ANSPRUCH_GRUNDLAGE) as AnspruchsGrundlage[]).map((g) => ({ value: g, label: ANSPRUCH_GRUNDLAGE[g] }))}
                  error={fehler.grundlage}
                />
                {grundlage !== "SATZUNG" ? (
                  <Input
                    name="grundlageVom"
                    type="date"
                    label={grundlage === "VERTRAG" ? "Vertrag vom" : "Beschluss vom"}
                    defaultValue={spende?.verzicht?.grundlageVom ?? ""}
                    max={heute}
                    required
                    error={fehler.grundlageVom}
                  />
                ) : (
                  <div />
                )}
                <Input
                  name="verzichtVom"
                  type="date"
                  label="Verzicht erklärt am"
                  defaultValue={spende?.verzicht?.verzichtVom ?? heute}
                  max={heute}
                  required
                  error={fehler.verzichtVom}
                />
              </DialogRow>
              <DialogNotice icon={<Info className="text-text-tertiary h-4 w-4" aria-hidden />}>
                Der Anspruch muss ernsthaft eingeräumt sein, bevor die Arbeit getan ist — durch Satzung, Vorstandsbeschluss oder Vertrag. Der Verzicht folgt danach (§ 10b Abs. 3 Satz 5 EStG). Auf der Bestätigung steht dann „Verzicht auf Erstattung von Aufwendungen: ja“.
              </DialogNotice>
            </DialogSection>
          )}
        </DialogContent>

        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>{spende ? "Speichern" : "Spende erfassen"}</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

/** Der Knopf mit seinem Dialog — fuer Seitenkopf, Aufruf und Spenderblatt. */
export function SpendeErfassenKnopf({
  aufrufe,
  vorgabe,
  label = "Spende erfassen",
  variant = "default",
  size = "md",
}: {
  aufrufe: AufrufWahl[];
  vorgabe?: Vorgabe;
  label?: string;
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
}) {
  const [offen, setOffen] = useState(false);
  return (
    <>
      <Button variant={variant} size={size} onClick={() => setOffen(true)}>
        <Plus className="h-4 w-4" aria-hidden />
        {label}
      </Button>
      {offen && (
        <SpendeDialog
          open
          onClose={() => setOffen(false)}
          vorgabe={vorgabe}
          aufrufe={aufrufe}
        />
      )}
    </>
  );
}
